import { startTransition, Suspense, useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { formatQuery } from "react-querybuilder";
import { ReactNodeDialog } from "../../utils/dialogUtlities";
import Card from "../Card";
import ChartSeriesConfig, { validChartSeriesConfig } from "./ChartSeriesConfig";
import DataTable from "../DataTable";
import { DefaultButton } from "../DefaultButton";
import { Col, Row } from "../Grid";
import NumericInput from "../NumericInput";
import TextInput from "../TextInput";
import PropTypes from 'prop-types';
import { Spinner } from "react-bootstrap";

export const validChartConfig = chart => {
    return chart?.title && chart.series?.length > 0;
}

/**
 * A component for editing the configuration of a Chart.js chart.
 * @component
 */
const ChartConfig = ({ chart, fields, onChange, disabled = false }) => {
    const [selectedSeries, setSelectedSeries] = useState();
    const { t } = useTranslation();

    const columns = useMemo(() => [
        { title: t('title'), type: 'text', readonly: true, editable: false, field: "label" },
        { title: t('color'), type: 'colorpicker', readonly: true, editable: false, field: "color" },
        //{ title: t('filter', { count: 1 }), type: 'text', readonly: true, editable: false, field: "query" },
        { title: t('independentAxis'), type: 'text', readonly: true, editable: false, field: "xField" },
        { title: t('collation'), type: 'text', readonly: true, editable: false, field: "collation.mode" },
        { title: '', key: 'edit', type: 'dynamic', def: row => <DefaultButton kind="edit" onClick={() => setSelectedSeries(row.orig)}></DefaultButton> }
    ], [t])

    const handleRemove = useCallback(toRemove => {
        const series = [...chart.series];
        const idx = series.findIndex(s => s.id === toRemove.id);
        if (idx > -1) {
            series.splice(idx, 1);
        }
        onChange({ ...chart, series });
    }, [chart, onChange])

    const displaySeries = useMemo(() => {
        return chart?.series?.map(s => ({...s, xField: fields.find(f => f.name === s.xField)?.label ?? s.xField, query: formatQuery(s.query, 'jsonlogic'), orig: s })) ?? []
    }, [chart?.series, fields]);

    return <>
        <ReactNodeDialog size="xlg" title={selectedSeries?.id ? t('editSeries') : t('addSeries')} showModal={!!selectedSeries} onSave={() => {
            if (selectedSeries?.id) {
                onChange({ ...chart, series: chart.series.map(s => s.id === selectedSeries.id ? selectedSeries : s)});
            } else {
                onChange({ ...chart, series: [...(chart?.series ?? []), { ...selectedSeries, id: (chart?.series?.reduce((max, cur) => cur.id > max ? cur.id : max, 0) ?? 0) + 1 }]});
            }
            setSelectedSeries();
        }} onClose={() => setSelectedSeries()} invalid={!validChartSeriesConfig(selectedSeries)}>
            {selectedSeries ? <Suspense fallback={<Spinner variant="primary"></Spinner>}><ChartSeriesConfig fields={fields} series={selectedSeries} onChange={v => setSelectedSeries(v)}></ChartSeriesConfig></Suspense> : null}
        </ReactNodeDialog>
        <Card title={t('chart')}>
            <Row>
                <Col md={6}>
                    <TextInput value={chart.title} placeholder={t('title')} disabled={disabled} required={true} onChange={title => onChange({ ...chart, title })}></TextInput>
                </Col>
                <Col md={3}>
                    <NumericInput value={chart.panel?.w} placeholder={t('width')} disabled={disabled} onChange={w => onChange({ ...chart, panel: { ...chart.panel, w }})}></NumericInput>
                </Col>
                <Col md={3}>
                    <NumericInput value={chart.panel?.h} placeholder={t('height')} disabled={disabled} onChange={h => onChange({ ...chart, panel: { ...chart.panel, h }})}></NumericInput>
                </Col>
            </Row>
            <Row>
                <Col md={3}>
                    <NumericInput value={chart.panel?.minW} placeholder={t('minWidth')} disabled={disabled} onChange={minW => onChange({ ...chart, panel: { ...chart.panel, minW }})}></NumericInput>
                </Col>
                <Col md={3}>
                    <NumericInput value={chart.panel?.minH} placeholder={t('minHeight')} disabled={disabled} onChange={minH => onChange({ ...chart, panel: { ...chart.panel, minH }})}></NumericInput>
                </Col>
                <Col md={3}>
                    <NumericInput value={chart.panel?.maxW} placeholder={t('maxWidth')} disabled={disabled} onChange={maxW => onChange({ ...chart, panel: { ...chart.panel, maxW }})}></NumericInput>
                </Col>
                <Col md={3}>
                    <NumericInput value={chart.panel?.maxH} placeholder={t('maxHeight')} disabled={disabled} onChange={maxH => onChange({ ...chart, panel: { ...chart.panel, maxH }})}></NumericInput>
                </Col>
            </Row>
        </Card>
        <Card title={t('series')} headerControls={disabled ? null : <DefaultButton kind="add" onClick={(e) => { e.stopPropagation(); e.preventDefault(); startTransition(() => setSelectedSeries({}))}}></DefaultButton>}>
            <DataTable columns={columns} value={displaySeries} onRemove={handleRemove} disableAdd={true} emptyMessage={"No series found"}></DataTable>
        </Card>
    </>

}

ChartConfig.propTypes = {
    chart: PropTypes.object,
    fields: PropTypes.arrayOf(PropTypes.object).isRequired,
    onChange: PropTypes.func.isRequired,
    disabled: PropTypes.bool
}

export default ChartConfig;